import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GripVertical, LayoutGrid } from 'lucide-react';
import { DEFAULT_OPERATIONAL_SECTION_ORDER, OPERATIONAL_SECTIONS } from '../../config/operationalTileCatalog';
import { customExecutiveMetricsService } from '../../services/customExecutiveMetricsService';

function reorderList(list, fromId, toId) {
  if (!fromId || !toId || fromId === toId) return list;
  const next = [...list];
  const fromIdx = next.indexOf(fromId);
  const toIdx = next.indexOf(toId);
  if (fromIdx < 0 || toIdx < 0) return list;
  next.splice(fromIdx, 1);
  next.splice(toIdx, 0, fromId);
  return next;
}

function SortableList({ title, itemIds, catalog, disabledIds, onToggle, onReorder, disabled, saving }) {
  const catalogById = useMemo(
    () => new Map(catalog.map((item) => [item.id, item])),
    [catalog]
  );

  const [dragId, setDragId] = useState(null);

  const handleDrop = (targetId) => {
    if (!dragId) return;
    onReorder(reorderList(itemIds, dragId, targetId));
    setDragId(null);
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <p className="text-sm font-medium text-gray-800 mb-1">{title}</p>
      <p className="text-xs text-gray-500 mb-3">Drag to reorder. Uncheck to hide from the operational dashboard.</p>
      <ul className="space-y-2">
        {itemIds.map((id) => {
          const item = catalogById.get(id);
          if (!item) return null;
          const hidden = disabledIds.has(id);
          return (
            <li
              key={id}
              draggable={!disabled && !saving}
              onDragStart={() => setDragId(id)}
              onDragEnd={() => setDragId(null)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(id)}
              className={`flex items-center gap-2 text-sm rounded-md px-2 py-1.5 border ${
                dragId === id ? 'border-blue-400 bg-blue-50' : 'border-transparent hover:border-gray-200 hover:bg-gray-50'
              }`}
            >
              <GripVertical className="w-4 h-4 text-gray-400 shrink-0 cursor-grab" />
              <input
                type="checkbox"
                checked={!hidden}
                onChange={() => onToggle(id)}
                disabled={disabled || saving}
              />
              <span className={hidden ? 'text-gray-400 line-through' : 'text-gray-800'}>{item.title}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function OperationalTileLayoutSection({ organizationId, disabled }) {
  const [layout, setLayout] = useState({
    disabledOperationalSectionIds: [],
    operationalSectionOrder: DEFAULT_OPERATIONAL_SECTION_ORDER,
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const loadLayout = useCallback(async () => {
    if (!organizationId) return;
    const data = await customExecutiveMetricsService.getLayoutOverrides(organizationId, {
      includeHistory: false,
    });
    const loaded = data.layout || {};
    setLayout({
      disabledOperationalSectionIds: loaded.disabledOperationalSectionIds || [],
      operationalSectionOrder: loaded.operationalSectionOrder?.length
        ? loaded.operationalSectionOrder
        : DEFAULT_OPERATIONAL_SECTION_ORDER,
    });
  }, [organizationId]);

  useEffect(() => {
    loadLayout();
  }, [loadLayout]);

  const toggleSection = (id) => {
    setLayout((prev) => {
      const set = new Set(prev.disabledOperationalSectionIds || []);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...prev, disabledOperationalSectionIds: [...set] };
    });
  };

  const handleSave = async () => {
    if (!organizationId) return;
    setSaving(true);
    setMessage('');
    try {
      const existing = await customExecutiveMetricsService.getLayoutOverrides(organizationId);
      await customExecutiveMetricsService.saveLayoutOverrides(
        organizationId,
        { ...(existing.layout || {}), ...layout },
        null
      );
      setMessage('Operational section layout saved for your organization.');
    } catch (e) {
      setMessage(e.message || 'Failed to save layout.');
    } finally {
      setSaving(false);
    }
  };

  const disabledSections = new Set(layout.disabledOperationalSectionIds || []);

  const sectionOrder = useMemo(() => {
    const known = new Set(DEFAULT_OPERATIONAL_SECTION_ORDER);
    const ordered = (layout.operationalSectionOrder || []).filter((id) => known.has(id));
    DEFAULT_OPERATIONAL_SECTION_ORDER.forEach((id) => {
      if (!ordered.includes(id)) ordered.push(id);
    });
    return ordered;
  }, [layout.operationalSectionOrder]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <LayoutGrid className="w-5 h-5 text-blue-600" />
          Operational section layout
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Show, hide, and reorder module panels on the Operational Dashboard. Custom highlight metrics
          you add separately always appear below the header.
        </p>
      </div>

      {disabled && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Select an organization to manage operational section layout.
        </p>
      )}

      <SortableList
        title="Module sections"
        itemIds={sectionOrder}
        catalog={OPERATIONAL_SECTIONS}
        disabledIds={disabledSections}
        onToggle={toggleSection}
        onReorder={(next) => setLayout((prev) => ({ ...prev, operationalSectionOrder: next }))}
        disabled={disabled}
        saving={saving}
      />

      <button
        type="button"
        onClick={handleSave}
        disabled={disabled || saving}
        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'Save section layout'}
      </button>
      {message && (
        <p className={`text-sm ${message.includes('Failed') ? 'text-red-600' : 'text-green-700'}`}>{message}</p>
      )}
    </div>
  );
}
